import React, {useEffect, useState} from "react";
import axios from "axios";
import Swal from "sweetalert2";
import {DataGrid, GridToolbar} from "@mui/x-data-grid";
import {baseUrl, ipAddress, userLocation, userName} from "../Univ.js";
import PatientIPSearch from "../PatientIPSearch";
function DietaryManagement() {
    // =========================================================
    // TABS
    // =========================================================
    const [tab, setTab] = useState("order");
    // =========================================================
    // MASTER DATA
    // =========================================================
    const [dietMaster, setDietMaster] = useState([]);
    const [departments, setDepartments] = useState([]);
    // =========================================================
    // TRANSACTION DATA
    // =========================================================
    const [orders, setOrders] = useState([]);
    const [meals, setMeals] = useState([]);
    const [stock, setStock] = useState([]);
    // =========================================================
    // DIET MASTER FORM
    // =========================================================
    const [dietForm, setDietForm] = useState({
        diet_code: "", diet_name: "", diet_type: "NORMAL", description: "", calories: "", active: true
    });
    // =========================================================
    // PATIENT DIET ORDER
    // =========================================================
    const [order, setOrder] = useState({
        ip_id: "",
        pat_id: "",
        patient_name: "",
        age: "",
        gender: "",
        department: "",
        ward: "",
        room: "",
        bed_no: "",
        diet_code: "",
        diet_name: "",
        meal_type: "BREAKFAST",
        order_date: new Date()
            .toISOString()
            .substring(0, 10),
        start_date: new Date()
            .toISOString()
            .substring(0, 10),
        end_date: "",
        quantity: 1,
        special_instruction: "",
        ordered_by: userName || ""
    });
    // =========================================================
    // MEAL PRODUCTION
    // =========================================================
    const [meal, setMeal] = useState({
        diet_order_id: "",
        meal_type: "BREAKFAST",
        meal_date: new Date()
            .toISOString()
            .substring(0, 10),
        preparation_status: "PENDING",
        prepared_quantity: 0,
        distributed_quantity: 0,
        refused_quantity: 0,
        distributed_by: userName || "",
        remarks: ""
    });
    // =========================================================
    // KITCHEN STOCK
    // =========================================================
    const [stockForm, setStockForm] = useState({
        item_code: "", item_name: "", category: "", unit: "KG", current_stock: "", reorder_level: "", expiry_date: ""
    });
    // =========================================================
    // LOAD INITIAL DATA
    // =========================================================
    useEffect(() => {
        loadData();
    }, []);
    // =========================================================
    // LOAD ALL DATA
    // =========================================================
    const loadData = () => {
        axios
            .get(`${baseUrl}dietary/master/`)
            .then(response => {
                setDietMaster(Array.isArray(response.data) ? response.data : response.data?.results || []);
            })
            .catch(error => {
                console.log("Diet master error:", error);
            });
        axios
            .get(`${baseUrl}add-dept/`)
            .then(response => {
                const data = Array.isArray(response.data) ? response.data : response.data?.results || [];
                setDepartments(data);
            })
            .catch(error => {
                console.log("Department error:", error);
            });
        axios
            .get(`${baseUrl}dietary/orders/`)
            .then(response => {
                setOrders(Array.isArray(response.data) ? response.data : response.data?.results || []);
            })
            .catch(error => {
                console.log("Diet orders error:", error);
            });
        axios
            .get(`${baseUrl}dietary/meals/`)
            .then(response => {
                setMeals(Array.isArray(response.data) ? response.data : response.data?.results || []);
            })
            .catch(error => {
                console.log("Meals error:", error);
            });
        axios
            .get(`${baseUrl}dietary/kitchen-stock/`)
            .then(response => {
                setStock(Array.isArray(response.data) ? response.data : response.data?.results || []);
            })
            .catch(error => {
                console.log("Kitchen stock error:", error);
            });
    };
    // =========================================================
    // DEPARTMENT NORMALIZER
    // =========================================================
    const getDepartmentName = (department, index) => {
        if (typeof department === "string") {
            return department;
        }
        return (department?.name || department?.department || department?.department_name || department?.dept_name || "");
    };
    // =========================================================
    // PATIENT SELECTION
    // =========================================================
    const selectPatient = data => {
        console.log("SELECTED PATIENT:", data);
        const department = data.department || data.department_name || data.dept_name || "";
        const ward = data.ward || data.ward_name || "";
        const room = data.room || data.room_no || data.room_number || data.room_name || "";
        const bed = data.bed_no || data.bed || data.bed_number || "";
        setOrder(prev => ({
            ...prev,
            ip_id: data.ip_id || data.ipId || "",
            pat_id: data.pat_id || data.patient_id || data.patId || "",
            patient_name: data?.patient_name || data?.patient || data?.pat?.patient || "",
            age: data.age || "",
            gender: data.gender || "",
            department: department,
            ward: ward,
            room: room,
            bed_no: bed
        }));
    };
    // =========================================================
    // DIET MASTER CHANGE
    // =========================================================
    const handleDietChange = e => {
        const {
            name, value
        } = e.target;
        setDietForm(prev => ({
            ...prev, [name]: value
        }));
    };
    // =========================================================
    // SAVE DIET MASTER
    // =========================================================
    const saveDiet = async e => {
        e.preventDefault();
        if (!dietForm.diet_code.trim()) {
            Swal.fire("Required", "Enter diet code.", "warning");
            return;
        }
        if (!dietForm.diet_name.trim()) {
            Swal.fire("Required", "Enter diet name.", "warning");
            return;
        }
        try {
            await axios.post(`${baseUrl}dietary/master/`, {
                ...dietForm,
                calories: dietForm.calories || null,
                addedby: userName,
                ip_add: ipAddress,
                location: userLocation
            });
            await Swal.fire({
                title: "Diet Saved",
                text: "Diet master created successfully.",
                icon: "success",
                timer: 1500,
                showConfirmButton: false
            });
            setDietForm({
                diet_code: "", diet_name: "", diet_type: "NORMAL", description: "", calories: "", active: true
            });
            loadData();
        } catch (error) {
            console.log(error);
            Swal.fire("Error", error?.response?.data?.error || "Unable to create diet.", "error");
        }
    };
    // =========================================================
    // DIET SELECTION
    // =========================================================
    const selectDiet = e => {
        const dietCode = e.target.value;
        const selected = dietMaster.find(item => item.diet_code === dietCode);
        setOrder(prev => ({
            ...prev, diet_code: selected?.diet_code || "", diet_name: selected?.diet_name || ""
        }));
    };
    // =========================================================
    // ORDER CHANGE
    // =========================================================
    const handleOrderChange = e => {
        const {
            name, value
        } = e.target;
        setOrder(prev => ({
            ...prev, [name]: value
        }));
    };
    // =========================================================
    // CREATE DIET ORDER
    // =========================================================
    const submitOrder = async e => {
        e.preventDefault();
        if (!order.ip_id && !order.pat_id) {
            Swal.fire("Patient Required", "Please select a patient/IP.", "warning");
            return;
        }
        if (!order.diet_code) {
            Swal.fire("Diet Required", "Please select a diet.", "warning");
            return;
        }
        if (!order.meal_type) {
            Swal.fire("Meal Required", "Please select meal type.", "warning");
            return;
        }
        try {
            const fd = new FormData();
            fd.append("order_no", "DIET-" + Date.now());
            /*
             * Send the fields used by your
             * existing dietary order API.
             */
            fd.append("ip_id", order.ip_id);
            fd.append("pat_id", order.pat_id);
            fd.append("patient_name", order.patient_name);
            fd.append("age", order.age);
            fd.append("gender", order.gender);
            fd.append("department", order.department);
            fd.append("ward", order.ward);
            fd.append("bed_no", order.bed_no);
            fd.append("diet_code", order.diet_code);
            fd.append("diet_name", order.diet_name);
            fd.append("meal_type", order.meal_type);
            fd.append("order_date", order.order_date);
            fd.append("start_date", order.start_date);
            fd.append("end_date", order.end_date);
            fd.append("quantity", order.quantity);
            fd.append("special_instruction", order.special_instruction);
            fd.append("ordered_by", order.ordered_by);
            fd.append("status", "ORDERED");
            fd.append("addedby", userName);
            fd.append("ip_add", ipAddress);
            fd.append("location", userLocation);
            await axios.post(`${baseUrl}dietary/orders/`, fd);
            await Swal.fire({
                title: "Diet Order Created",
                text: "Patient diet order created successfully.",
                icon: "success",
                timer: 1600,
                showConfirmButton: false
            });
            setOrder({
                ip_id: "",
                pat_id: "",
                patient_name: "",
                age: "",
                gender: "",
                department: "",
                ward: "",
                room: "",
                bed_no: "",
                diet_code: "",
                diet_name: "",
                meal_type: "BREAKFAST",
                order_date: new Date()
                    .toISOString()
                    .substring(0, 10),
                start_date: new Date()
                    .toISOString()
                    .substring(0, 10),
                end_date: "",
                quantity: 1,
                special_instruction: "",
                ordered_by: userName || ""
            });
            loadData();
        } catch (error) {
            console.log(error);
            Swal.fire("Error", error?.response?.data?.error || "Unable to create diet order.", "error");
        }
    };
    // =========================================================
    // MEAL CHANGE
    // =========================================================
    const handleMealChange = e => {
        const {
            name, value
        } = e.target;
        setMeal(prev => ({
            ...prev, [name]: value
        }));
    };
    // =========================================================
    // SAVE MEAL
    // =========================================================
    const submitMeal = async e => {
        e.preventDefault();
        if (!meal.diet_order_id) {
            Swal.fire("Required", "Please select a diet order.", "warning");
            return;
        }
        try {
            const fd = new FormData();
            fd.append("meal_no", "MEAL-" + Date.now());
            Object.keys(meal)
                .forEach(key => {
                    fd.append(key, meal[key]);
                });
            fd.append("addedby", userName);
            fd.append("ip_add", ipAddress);
            await axios.post(`${baseUrl}dietary/meals/`, fd);
            await Swal.fire({
                title: "Meal Saved",
                text: "Meal production entry saved successfully.",
                icon: "success",
                timer: 1500,
                showConfirmButton: false
            });
            setMeal({
                diet_order_id: "",
                meal_type: "BREAKFAST",
                meal_date: new Date()
                    .toISOString()
                    .substring(0, 10),
                preparation_status: "PENDING",
                prepared_quantity: 0,
                distributed_quantity: 0,
                refused_quantity: 0,
                distributed_by: userName || "",
                remarks: ""
            });
            loadData();
        } catch (error) {
            console.log(error);
            Swal.fire("Error", error?.response?.data?.error || "Unable to save meal production.", "error");
        }
    };
    // =========================================================
    // STOCK CHANGE
    // =========================================================
    const handleStockChange = e => {
        const {
            name, value
        } = e.target;
        setStockForm(prev => ({
            ...prev, [name]: value
        }));
    };
    // =========================================================
    // SAVE STOCK
    // =========================================================
    const submitStock = async e => {
        e.preventDefault();
        if (!stockForm.item_code.trim()) {
            Swal.fire("Required", "Enter item code.", "warning");
            return;
        }
        if (!stockForm.item_name.trim()) {
            Swal.fire("Required", "Enter item name.", "warning");
            return;
        }
        try {
            const fd = new FormData();
            Object.keys(stockForm)
                .forEach(key => {
                    fd.append(key, stockForm[key]);
                });
            fd.append("status", "ACTIVE");
            fd.append("addedby", userName);
            fd.append("ip_add", ipAddress);
            fd.append("location", userLocation);
            await axios.post(`${baseUrl}dietary/kitchen-stock/`, fd);
            await Swal.fire({
                title: "Stock Added",
                text: "Kitchen stock item added successfully.",
                icon: "success",
                timer: 1500,
                showConfirmButton: false
            });
            setStockForm({
                item_code: "",
                item_name: "",
                category: "",
                unit: "KG",
                current_stock: "",
                reorder_level: "",
                expiry_date: ""
            });
            loadData();
        } catch (error) {
            console.log(error);
            Swal.fire("Error", error?.response?.data?.error || "Unable to add kitchen stock.", "error");
        }
    };
    // =========================================================
    // DIET MASTER COLUMNS
    // =========================================================
    const dietColumns = [{
        field: "id", headerName: "ID", width: 70
    }, {
        field: "diet_code", headerName: "Diet Code", width: 140
    }, {
        field: "diet_name", headerName: "Diet Name", width: 220
    }, {
        field: "diet_type", headerName: "Diet Type", width: 150
    }, {
        field: "calories", headerName: "Calories", width: 110
    }, {
        field: "description", headerName: "Description", width: 300
    }, {
        field: "active", headerName: "Active", width: 100, valueGetter: value => {
            if (value === true || value === 1 || value === "1") {
                return "Yes";
            }
            return "No";
        }
    }];
    // =========================================================
    // ORDER COLUMNS
    // =========================================================
    const orderColumns = [{
        field: "order_no", headerName: "Order No", width: 160
    }, {
        field: "ip_id", headerName: "IP ID", width: 140
    }, {
        field: "pat_id", headerName: "Patient ID", width: 150
    }, {
        field: "patient_name", headerName: "Patient", width: 200
    }, {
        field: "department", headerName: "Department", width: 160
    }, {
        field: "ward", headerName: "Ward", width: 130
    }, {
        field: "room", headerName: "Room", width: 110
    }, {
        field: "bed_no", headerName: "Bed", width: 100
    }, {
        field: "diet_name", headerName: "Diet", width: 200
    }, {
        field: "meal_type", headerName: "Meal", width: 120
    }, {
        field: "quantity", headerName: "Qty", width: 80
    }, {
        field: "start_date", headerName: "Start", width: 120
    }, {
        field: "end_date", headerName: "End", width: 120
    }, {
        field: "status", headerName: "Status", width: 120
    }];
    // =========================================================
    // MEAL COLUMNS
    // =========================================================
    const mealColumns = [{
        field: "meal_no", headerName: "Meal No", width: 160
    }, {
        field: "diet_order_id", headerName: "Diet Order", width: 130
    }, {
        field: "meal_type", headerName: "Meal", width: 120
    }, {
        field: "meal_date", headerName: "Date", width: 120
    }, {
        field: "prepared_quantity", headerName: "Prepared", width: 110
    }, {
        field: "distributed_quantity", headerName: "Distributed", width: 120
    }, {
        field: "refused_quantity", headerName: "Refused", width: 100
    }, {
        field: "preparation_status", headerName: "Status", width: 130
    }, {
        field: "distributed_by", headerName: "Distributed By", width: 160
    }];
    // =========================================================
    // STOCK COLUMNS
    // =========================================================
    const stockColumns = [{
        field: "id", headerName: "ID", width: 70
    }, {
        field: "item_code", headerName: "Item Code", width: 140
    }, {
        field: "item_name", headerName: "Item Name", width: 220
    }, {
        field: "category", headerName: "Category", width: 160
    }, {
        field: "unit", headerName: "Unit", width: 100
    }, {
        field: "current_stock", headerName: "Current Stock", width: 130
    }, {
        field: "reorder_level", headerName: "Reorder Level", width: 130
    }, {
        field: "expiry_date", headerName: "Expiry", width: 130
    }, {
        field: "status", headerName: "Status", width: 120
    }];
    // =========================================================
    // RETURN
    // =========================================================
    return (<section className="container-fluid mt-4">


        {/* =================================================
                HEADER
            ================================================= */}

        <h3 className="text-center bg-success text-white p-2">

            <i className="fa fa-utensils me-2"></i>

            Dietary Management

        </h3>


        {/* =================================================
                TABS
            ================================================= */}

        <div className="btn-group mb-3">

            <button
                className={`btn ${tab === "order" ? "btn-primary" : "btn-outline-primary"}`}
                onClick={() => setTab("order")}
            >

                <i className="fa fa-user me-1"></i>

                Patient Diet Order

            </button>


            <button
                className={`btn ${tab === "meal" ? "btn-success" : "btn-outline-success"}`}
                onClick={() => setTab("meal")}
            >

                <i className="fa fa-kitchen-set me-1"></i>

                Meal Production

            </button>


            <button
                className={`btn ${tab === "stock" ? "btn-warning" : "btn-outline-warning"}`}
                onClick={() => setTab("stock")}
            >

                <i className="fa fa-boxes-stacked me-1"></i>

                Kitchen Stock

            </button>


            <button
                className={`btn ${tab === "master" ? "btn-info" : "btn-outline-info"}`}
                onClick={() => setTab("master")}
            >

                <i className="fa fa-database me-1"></i>

                Diet Master

            </button>


            <button
                className={`btn ${tab === "report" ? "btn-secondary" : "btn-outline-secondary"}`}
                onClick={() => setTab("report")}
            >

                <i className="fa fa-chart-column me-1"></i>

                Reports

            </button>

        </div>


        {/* =================================================
                PATIENT DIET ORDER
            ================================================= */}

        {tab === "order" && (<>


            <PatientIPSearch
                onSelect={selectPatient}
            />


            <div className="card shadow-sm mb-4">


                <div className="card-header bg-primary text-white">

                    <h5 className="mb-0">

                        <i className="fa fa-utensils me-2"></i>

                        Patient Dietary Order

                    </h5>

                </div>


                <div className="card-body">


                    <form onSubmit={submitOrder}>


                        <div className="row">


                            {/* IP */}

                            <div className="col-md-2 mb-3">

                                <label>
                                    IP ID
                                </label>

                                <input
                                    className="form-control"
                                    value={order.ip_id}
                                    readOnly
                                />

                            </div>


                            {/* Patient ID */}

                            <div className="col-md-2 mb-3">

                                <label>
                                    Patient ID
                                </label>

                                <input
                                    className="form-control"
                                    value={order.pat_id}
                                    readOnly
                                />

                            </div>


                            {/* Patient */}

                            <div className="col-md-3 mb-3">

                                <label>
                                    Patient
                                </label>

                                <input
                                    className="form-control"
                                    value={order.patient_name}
                                    readOnly
                                />

                            </div>


                            {/* Age */}

                            <div className="col-md-1 mb-3">

                                <label>
                                    Age
                                </label>

                                <input
                                    className="form-control"
                                    value={order.age}
                                    readOnly
                                />

                            </div>


                            {/* Gender */}

                            <div className="col-md-2 mb-3">

                                <label>
                                    Gender
                                </label>

                                <input
                                    className="form-control"
                                    value={order.gender}
                                    readOnly
                                />

                            </div>


                            {/* Department */}

                            <div className="col-md-2 mb-3">

                                <label>
                                    Department
                                </label>

                                <select
                                    className="form-control"
                                    name="department"
                                    value={order.department}
                                    onChange={handleOrderChange}
                                >

                                    <option value="">
                                        Select Department
                                    </option>


                                    {departments.map((department, index) => {
                                        const value = getDepartmentName(department, index);
                                        return (<option
                                            key={department?.id || index}
                                            value={value}
                                        >

                                            {value}

                                        </option>);
                                    })}

                                </select>

                            </div>


                            {/* Ward */}

                            <div className="col-md-2 mb-3">

                                <label>
                                    Ward
                                </label>

                                <input
                                    className="form-control"
                                    value={order.ward}
                                    readOnly
                                />

                            </div>


                            {/* Room */}

                            <div className="col-md-2 mb-3">

                                <label>
                                    Room
                                </label>

                                <input
                                    className="form-control"
                                    value={order.room}
                                    readOnly
                                />

                            </div>


                            {/* Bed */}

                            <div className="col-md-2 mb-3">

                                <label>
                                    Bed
                                </label>

                                <input
                                    className="form-control"
                                    value={order.bed_no}
                                    readOnly
                                />

                            </div>


                            {/* Diet */}

                            <div className="col-md-4 mb-3">

                                <label>
                                    Diet
                                </label>

                                <select
                                    className="form-control"
                                    value={order.diet_code}
                                    onChange={selectDiet}
                                >

                                    <option value="">
                                        Select Diet
                                    </option>


                                    {dietMaster
                                        .filter(item => item.active === true || item.active === 1 || item.active === "1" || item.active === undefined)
                                        .map(item => (<option
                                            key={item.id}
                                            value={item.diet_code}
                                        >

                                            {item.diet_code}
                                            {" - "}
                                            {item.diet_name}

                                        </option>))}

                                </select>

                            </div>


                            {/* Meal */}

                            <div className="col-md-3 mb-3">

                                <label>
                                    Meal
                                </label>

                                <select
                                    className="form-control"
                                    name="meal_type"
                                    value={order.meal_type}
                                    onChange={handleOrderChange}
                                >

                                    <option value="BREAKFAST">
                                        Breakfast
                                    </option>

                                    <option value="TEA">
                                        Tea
                                    </option>

                                    <option value="LUNCH">
                                        Lunch
                                    </option>

                                    <option value="SNACKS">
                                        Snacks
                                    </option>

                                    <option value="DINNER">
                                        Dinner
                                    </option>

                                </select>

                            </div>


                            {/* Quantity */}

                            <div className="col-md-2 mb-3">

                                <label>
                                    Quantity
                                </label>

                                <input
                                    type="number"
                                    min="1"
                                    className="form-control"
                                    name="quantity"
                                    value={order.quantity}
                                    onChange={handleOrderChange}
                                />

                            </div>


                            {/* Start Date */}

                            <div className="col-md-2 mb-3">

                                <label>
                                    Start Date
                                </label>

                                <input
                                    type="date"
                                    className="form-control"
                                    name="start_date"
                                    value={order.start_date}
                                    onChange={handleOrderChange}
                                />

                            </div>


                            {/* End Date */}

                            <div className="col-md-2 mb-3">

                                <label>
                                    End Date
                                </label>

                                <input
                                    type="date"
                                    className="form-control"
                                    name="end_date"
                                    value={order.end_date}
                                    onChange={handleOrderChange}
                                />

                            </div>


                            {/* Instructions */}

                            <div className="col-md-10 mb-3">

                                <label>
                                    Special Instructions
                                </label>

                                <textarea
                                    className="form-control"
                                    rows="3"
                                    name="special_instruction"
                                    value={order.special_instruction}
                                    onChange={handleOrderChange}
                                    placeholder="Allergy, food preference, fasting instruction, special diet instructions..."
                                />

                            </div>


                            {/* Save */}

                            <div className="col-md-2 mb-3 d-flex align-items-end">

                                <button
                                    type="submit"
                                    className="btn btn-success w-100"
                                >

                                    <i className="fa fa-save me-1"></i>

                                    Create Order

                                </button>

                            </div>


                        </div>


                    </form>


                </div>

            </div>


            {/* ORDER GRID */}

            <DataGrid

                rows={orders}

                columns={orderColumns}

                getRowId={row => row.id || row.order_no}

                autoHeight

                slots={{
                    toolbar: GridToolbar
                }}

                pageSizeOptions={[10, 25, 50, 100]}

            />

        </>)}


        {/* =================================================
                MEAL PRODUCTION
            ================================================= */}

        {tab === "meal" && (<div className="card shadow-sm">


            <div className="card-header bg-success text-white">

                <h5 className="mb-0">

                    <i className="fa fa-kitchen-set me-2"></i>

                    Kitchen Meal Production

                </h5>

            </div>


            <div className="card-body">


                <form onSubmit={submitMeal}>


                    <div className="row">


                        {/* Order */}

                        <div className="col-md-4 mb-3">

                            <label>
                                Diet Order
                            </label>

                            <select
                                className="form-control"
                                name="diet_order_id"
                                value={meal.diet_order_id}
                                onChange={handleMealChange}
                            >

                                <option value="">
                                    Select Diet Order
                                </option>


                                {orders.map(item => (<option
                                    key={item.id}
                                    value={item.id}
                                >

                                    {item.order_no}
                                    {" | "}
                                    {item.patient_name}
                                    {" | "}
                                    {item.diet_name}

                                </option>))}

                            </select>

                        </div>


                        {/* Meal */}

                        <div className="col-md-2 mb-3">

                            <label>
                                Meal
                            </label>

                            <select
                                className="form-control"
                                name="meal_type"
                                value={meal.meal_type}
                                onChange={handleMealChange}
                            >

                                <option value="BREAKFAST">
                                    Breakfast
                                </option>

                                <option value="TEA">
                                    Tea
                                </option>

                                <option value="LUNCH">
                                    Lunch
                                </option>

                                <option value="SNACKS">
                                    Snacks
                                </option>

                                <option value="DINNER">
                                    Dinner
                                </option>

                            </select>

                        </div>


                        {/* Date */}

                        <div className="col-md-2 mb-3">

                            <label>
                                Meal Date
                            </label>

                            <input
                                type="date"
                                className="form-control"
                                name="meal_date"
                                value={meal.meal_date}
                                onChange={handleMealChange}
                            />

                        </div>


                        {/* Status */}

                        <div className="col-md-2 mb-3">

                            <label>
                                Preparation Status
                            </label>

                            <select
                                className="form-control"
                                name="preparation_status"
                                value={meal.preparation_status}
                                onChange={handleMealChange}
                            >

                                <option value="PENDING">
                                    Pending
                                </option>

                                <option value="PREPARING">
                                    Preparing
                                </option>

                                <option value="PREPARED">
                                    Prepared
                                </option>

                                <option value="DISTRIBUTED">
                                    Distributed
                                </option>

                                <option value="CANCELLED">
                                    Cancelled
                                </option>

                            </select>

                        </div>


                        {/* Prepared */}

                        <div className="col-md-2 mb-3">

                            <label>
                                Prepared
                            </label>

                            <input
                                type="number"
                                min="0"
                                className="form-control"
                                name="prepared_quantity"
                                value={meal.prepared_quantity}
                                onChange={handleMealChange}
                            />

                        </div>


                        {/* Distributed */}

                        <div className="col-md-2 mb-3">

                            <label>
                                Distributed
                            </label>

                            <input
                                type="number"
                                min="0"
                                className="form-control"
                                name="distributed_quantity"
                                value={meal.distributed_quantity}
                                onChange={handleMealChange}
                            />

                        </div>


                        {/* Refused */}

                        <div className="col-md-2 mb-3">

                            <label>
                                Refused
                            </label>

                            <input
                                type="number"
                                min="0"
                                className="form-control"
                                name="refused_quantity"
                                value={meal.refused_quantity}
                                onChange={handleMealChange}
                            />

                        </div>


                        {/* Distributed By */}

                        <div className="col-md-3 mb-3">

                            <label>
                                Distributed By
                            </label>

                            <input
                                className="form-control"
                                name="distributed_by"
                                value={meal.distributed_by}
                                onChange={handleMealChange}
                            />

                        </div>


                        {/* Remarks */}

                        <div className="col-md-5 mb-3">

                            <label>
                                Remarks
                            </label>

                            <input
                                className="form-control"
                                name="remarks"
                                value={meal.remarks}
                                onChange={handleMealChange}
                            />

                        </div>


                        {/* Save */}

                        <div className="col-md-2 mb-3 d-flex align-items-end">

                            <button
                                type="submit"
                                className="btn btn-success w-100"
                            >

                                <i className="fa fa-save me-1"></i>

                                Save Meal

                            </button>

                        </div>


                    </div>


                </form>


                <hr/>


                <DataGrid

                    rows={meals}

                    columns={mealColumns}

                    getRowId={row => row.id || row.meal_no}

                    autoHeight

                    slots={{
                        toolbar: GridToolbar
                    }}

                    pageSizeOptions={[10, 25, 50, 100]}

                />

            </div>

        </div>)}


        {/* =================================================
                KITCHEN STOCK
            ================================================= */}

        {tab === "stock" && (<div className="card shadow-sm">


            <div className="card-header bg-warning">

                <h5 className="mb-0">

                    <i className="fa fa-boxes-stacked me-2"></i>

                    Kitchen Stock Management

                </h5>

            </div>


            <div className="card-body">


                <form onSubmit={submitStock}>


                    <div className="row">


                        {/* Code */}

                        <div className="col-md-2 mb-3">

                            <label>
                                Item Code
                            </label>

                            <input
                                className="form-control"
                                name="item_code"
                                value={stockForm.item_code}
                                onChange={handleStockChange}
                                required
                            />

                        </div>


                        {/* Name */}

                        <div className="col-md-3 mb-3">

                            <label>
                                Item Name
                            </label>

                            <input
                                className="form-control"
                                name="item_name"
                                value={stockForm.item_name}
                                onChange={handleStockChange}
                                required
                            />

                        </div>


                        {/* Category */}

                        <div className="col-md-2 mb-3">

                            <label>
                                Category
                            </label>

                            <select
                                className="form-control"
                                name="category"
                                value={stockForm.category}
                                onChange={handleStockChange}
                            >

                                <option value="">
                                    Select Category
                                </option>

                                <option value="GRAINS">
                                    Grains
                                </option>

                                <option value="PULSES">
                                    Pulses
                                </option>

                                <option value="VEGETABLES">
                                    Vegetables
                                </option>

                                <option value="FRUITS">
                                    Fruits
                                </option>

                                <option value="MILK">
                                    Milk / Dairy
                                </option>

                                <option value="MEAT">
                                    Meat
                                </option>

                                <option value="EGG">
                                    Egg
                                </option>

                                <option value="SPICES">
                                    Spices
                                </option>

                                <option value="OIL">
                                    Oil
                                </option>

                                <option value="BEVERAGES">
                                    Beverages
                                </option>

                                <option value="OTHER">
                                    Other
                                </option>

                            </select>

                        </div>


                        {/* Unit */}

                        <div className="col-md-2 mb-3">

                            <label>
                                Unit
                            </label>

                            <select
                                className="form-control"
                                name="unit"
                                value={stockForm.unit}
                                onChange={handleStockChange}
                            >

                                <option value="KG">
                                    KG
                                </option>

                                <option value="GRAM">
                                    Gram
                                </option>

                                <option value="LITRE">
                                    Litre
                                </option>

                                <option value="ML">
                                    ML
                                </option>

                                <option value="NUMBER">
                                    Number
                                </option>

                                <option value="PACK">
                                    Pack
                                </option>

                                <option value="BOTTLE">
                                    Bottle
                                </option>

                            </select>

                        </div>


                        {/* Current */}

                        <div className="col-md-2 mb-3">

                            <label>
                                Current Stock
                            </label>

                            <input
                                type="number"
                                step="0.01"
                                min="0"
                                className="form-control"
                                name="current_stock"
                                value={stockForm.current_stock}
                                onChange={handleStockChange}
                            />

                        </div>


                        {/* Reorder */}

                        <div className="col-md-2 mb-3">

                            <label>
                                Reorder Level
                            </label>

                            <input
                                type="number"
                                step="0.01"
                                min="0"
                                className="form-control"
                                name="reorder_level"
                                value={stockForm.reorder_level}
                                onChange={handleStockChange}
                            />

                        </div>


                        {/* Expiry */}

                        <div className="col-md-2 mb-3">

                            <label>
                                Expiry Date
                            </label>

                            <input
                                type="date"
                                className="form-control"
                                name="expiry_date"
                                value={stockForm.expiry_date}
                                onChange={handleStockChange}
                            />

                        </div>


                        {/* Save */}

                        <div className="col-md-2 mb-3 d-flex align-items-end">

                            <button
                                type="submit"
                                className="btn btn-success w-100"
                            >

                                <i className="fa fa-save me-1"></i>

                                Add Stock

                            </button>

                        </div>


                    </div>


                </form>


                <hr/>


                <DataGrid

                    rows={stock}

                    columns={stockColumns}

                    getRowId={row => row.id || row.item_code}

                    autoHeight

                    slots={{
                        toolbar: GridToolbar
                    }}

                    pageSizeOptions={[10, 25, 50, 100]}

                />

            </div>

        </div>)}


        {/* =================================================
                DIET MASTER
            ================================================= */}

        {tab === "master" && (<div className="card shadow-sm">


            <div className="card-header bg-info text-white">

                <h5 className="mb-0">

                    <i className="fa fa-database me-2"></i>

                    Dietary Master

                </h5>

            </div>


            <div className="card-body">


                <form onSubmit={saveDiet}>


                    <div className="row">


                        {/* Code */}

                        <div className="col-md-2 mb-3">

                            <label>
                                Diet Code
                            </label>

                            <input
                                className="form-control"
                                name="diet_code"
                                value={dietForm.diet_code}
                                onChange={handleDietChange}
                                placeholder="DIET-001"
                                required
                            />

                        </div>


                        {/* Name */}

                        <div className="col-md-3 mb-3">

                            <label>
                                Diet Name
                            </label>

                            <input
                                className="form-control"
                                name="diet_name"
                                value={dietForm.diet_name}
                                onChange={handleDietChange}
                                placeholder="Normal Diet"
                                required
                            />

                        </div>


                        {/* Type */}

                        <div className="col-md-2 mb-3">

                            <label>
                                Diet Type
                            </label>

                            <select
                                className="form-control"
                                name="diet_type"
                                value={dietForm.diet_type}
                                onChange={handleDietChange}
                            >

                                <option value="NORMAL">
                                    Normal
                                </option>

                                <option value="LIQUID">
                                    Liquid
                                </option>

                                <option value="SOFT">
                                    Soft
                                </option>

                                <option value="DIABETIC">
                                    Diabetic
                                </option>

                                <option value="CARDIAC">
                                    Cardiac
                                </option>

                                <option value="RENAL">
                                    Renal
                                </option>

                                <option value="LOW_SALT">
                                    Low Salt
                                </option>

                                <option value="HIGH_PROTEIN">
                                    High Protein
                                </option>

                                <option value="NPO">
                                    NPO
                                </option>

                                <option value="SPECIAL">
                                    Special
                                </option>

                            </select>

                        </div>


                        {/* Calories */}

                        <div className="col-md-2 mb-3">

                            <label>
                                Calories
                            </label>

                            <input
                                type="number"
                                min="0"
                                className="form-control"
                                name="calories"
                                value={dietForm.calories}
                                onChange={handleDietChange}
                            />

                        </div>


                        {/* Description */}

                        <div className="col-md-2 mb-3">

                            <label>
                                Description
                            </label>

                            <input
                                className="form-control"
                                name="description"
                                value={dietForm.description}
                                onChange={handleDietChange}
                            />

                        </div>


                        {/* Active */}

                        <div className="col-md-1 mb-3">

                            <label>
                                Status
                            </label>

                            <select
                                className="form-control"
                                name="active"
                                value={dietForm.active ? "true" : "false"}
                                onChange={e => setDietForm(prev => ({
                                    ...prev, active: e.target.value === "true"
                                }))}
                            >

                                <option value="true">
                                    Active
                                </option>

                                <option value="false">
                                    Inactive
                                </option>

                            </select>

                        </div>


                        {/* Save */}

                        <div className="col-md-2 mb-3 d-flex align-items-end">

                            <button
                                type="submit"
                                className="btn btn-success w-100"
                            >

                                <i className="fa fa-save me-1"></i>

                                Save Diet

                            </button>

                        </div>


                    </div>


                </form>


                <hr/>


                <DataGrid

                    rows={dietMaster}

                    columns={dietColumns}

                    getRowId={row => row.id || row.diet_code}

                    autoHeight

                    slots={{
                        toolbar: GridToolbar
                    }}

                    pageSizeOptions={[10, 25, 50, 100]}

                />

            </div>

        </div>)}


        {/* =================================================
                REPORTS
            ================================================= */}

        {tab === "report" && (<div className="row">


            <div className="col-md-3 mb-3">

                <div className="card shadow-sm text-center">

                    <div className="card-body">

                        <h6>
                            Active Diets
                        </h6>

                        <h2 className="text-success">

                            {dietMaster.filter(item => item.active === true || item.active === 1 || item.active === "1").length}

                        </h2>

                    </div>

                </div>

            </div>


            <div className="col-md-3 mb-3">

                <div className="card shadow-sm text-center">

                    <div className="card-body">

                        <h6>
                            Diet Orders
                        </h6>

                        <h2 className="text-primary">

                            {orders.length}

                        </h2>

                    </div>

                </div>

            </div>


            <div className="col-md-3 mb-3">

                <div className="card shadow-sm text-center">

                    <div className="card-body">

                        <h6>
                            Meal Production
                        </h6>

                        <h2 className="text-success">

                            {meals.length}

                        </h2>

                    </div>

                </div>

            </div>


            <div className="col-md-3 mb-3">

                <div className="card shadow-sm text-center">

                    <div className="card-body">

                        <h6>
                            Kitchen Stock Items
                        </h6>

                        <h2 className="text-warning">

                            {stock.length}

                        </h2>

                    </div>

                </div>

            </div>


            {/* =================================================
                        LOW STOCK
                    ================================================= */}

            <div className="col-md-12 mt-3">

                <div className="card shadow-sm">

                    <div className="card-header bg-danger text-white">

                        <strong>

                            <i className="fa fa-triangle-exclamation me-2"></i>

                            Low Kitchen Stock

                        </strong>

                    </div>


                    <div className="card-body">

                        <DataGrid

                            rows={stock.filter(item => {
                                const current = Number(item.current_stock);
                                const reorder = Number(item.reorder_level);
                                return (!isNaN(current) && !isNaN(reorder) && current <= reorder);
                            })}

                            columns={stockColumns}

                            getRowId={row => row.id || row.item_code}

                            autoHeight

                            slots={{
                                toolbar: GridToolbar
                            }}

                            pageSizeOptions={[10, 25, 50]}

                        />

                    </div>

                </div>

            </div>


        </div>)}

    </section>);
}
export default DietaryManagement;